// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { InvoiceAgingBucket, InvoiceListItem } from '@/billing/api/types';
import {
  AGING_CHIP_LABELS,
  agingChipSx,
} from '@/billing/components/invoice-status-constants';
import { InvoiceStatusChip } from '@/components/invoice-status-chip';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { formatShortDate } from '@/utilities/date-time';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { MouseEvent } from 'react';

interface InvoiceTableRowProps {
  invoice: InvoiceListItem;
  onClick: (invoice: InvoiceListItem, event: MouseEvent<HTMLElement>) => void;
}

function formatInvoiceTimestamp(dt: DateTime): string {
  return dt.toLocaleString(DateTime.DATETIME_MED);
}

// Returns an overdue-bucket key when the invoice is actually overdue, or null
// for DRAFT / PAID / VOID / not-yet-due OPEN invoices. The backend already
// computes aging_bucket so we just trust it — this thin wrapper only narrows
// 'current' (and non-open statuses) to null so the call site can conditionally
// render the chip.
function overdueBucket(
  invoice: InvoiceListItem
): Exclude<InvoiceAgingBucket, 'current'> | null {
  if (invoice.status !== 'OPEN' && invoice.status !== 'PARTIALLY_PAID') {
    return null;
  }
  if (invoice.agingBucket === 'current') {
    return null;
  }
  return invoice.agingBucket;
}

// Conditional rendering for the due date cell. DRAFT/VOID show just the date;
// PAID shows the date plus a "Paid [date]" caption; overdue OPEN /
// PARTIALLY_PAID show the date with an inline aging chip and "N days ago"
// caption; not-yet-due OPEN / PARTIALLY_PAID show the date plus "Due today"
// or "N days from now".
function DueDateCell({ invoice }: { invoice: InvoiceListItem }) {
  if (!invoice.dueDate) {
    return (
      <Typography variant="body2" color="text.secondary">
        {'\u2014'}
      </Typography>
    );
  }

  const dueDateLabel = formatShortDate(invoice.dueDate);

  if (invoice.status === 'DRAFT' || invoice.status === 'VOID') {
    return (
      <Typography variant="body2" color="text.secondary">
        {dueDateLabel}
      </Typography>
    );
  }

  if (invoice.status === 'PAID') {
    return (
      <Stack>
        <Typography variant="body2">{dueDateLabel}</Typography>
        {invoice.paidDate && (
          <Typography variant="caption" color="text.secondary">
            Paid {formatShortDate(invoice.paidDate)}
          </Typography>
        )}
      </Stack>
    );
  }

  const bucket = overdueBucket(invoice);
  if (bucket && invoice.daysOverdue > 0) {
    return (
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">{dueDateLabel}</Typography>
          <Chip
            label={AGING_CHIP_LABELS[bucket]}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              ...agingChipSx(bucket),
            }}
          />
        </Stack>
        <Typography variant="caption" color="error.main">
          {pluralize(invoice.daysOverdue, { singular: 'day' })} ago
        </Typography>
      </Stack>
    );
  }

  // OPEN / PARTIALLY_PAID, not overdue — show "Due today" or "N days from now".
  // dueDate is guaranteed non-null here (early return at the top handles null).
  const now = DateTime.now().startOf('day');
  const daysUntilDue = Math.max(
    0,
    Math.ceil(invoice.dueDate!.startOf('day').diff(now, 'days').days)
  );
  return (
    <Stack>
      <Typography variant="body2">{dueDateLabel}</Typography>
      <Typography variant="caption" color="text.secondary">
        {daysUntilDue === 0
          ? 'Due today'
          : `${pluralize(daysUntilDue, { singular: 'day' })} from now`}
      </Typography>
    </Stack>
  );
}

// Lifecycle tooltip content — one line per stamped lifecycle column. The
// drafted_at column is always set, so the tooltip always shows at least that.
function lifecycleTooltipLines(invoice: InvoiceListItem): string[] {
  const lines = [`Drafted: ${formatInvoiceTimestamp(invoice.draftedAt)}`];
  if (invoice.openedAt) {
    lines.push(`Opened: ${formatInvoiceTimestamp(invoice.openedAt)}`);
  }
  if (invoice.firstSentAt) {
    lines.push(`First sent: ${formatInvoiceTimestamp(invoice.firstSentAt)}`);
  }
  if (
    invoice.lastSentAt &&
    (!invoice.firstSentAt || !invoice.lastSentAt.equals(invoice.firstSentAt))
  ) {
    lines.push(`Last sent: ${formatInvoiceTimestamp(invoice.lastSentAt)}`);
  }
  if (invoice.paidDate) {
    lines.push(`Paid: ${formatShortDate(invoice.paidDate)}`);
  }
  if (invoice.voidedAt) {
    lines.push(`Voided: ${formatInvoiceTimestamp(invoice.voidedAt)}`);
  }
  return lines;
}

export function InvoiceTableRow(props: InvoiceTableRowProps) {
  const { invoice } = props;
  const balanceNumber = invoice.balance.amount.toNumber();
  const hasBalance = !Number.isNaN(balanceNumber) && balanceNumber > 0;
  const tooltipLines = lifecycleTooltipLines(invoice);

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer' }}
      onClick={(event) => props.onClick(invoice, event)}
    >
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {invoice.invoiceNumber}
        </Typography>
      </TableCell>
      <TableCell>
        {invoice.customerName ?? (
          <Typography variant="body2" color="text.secondary">
            {'\u2014'}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {invoice.projectName ?? (
          <Typography variant="body2" color="text.secondary">
            {'\u2014'}
          </Typography>
        )}
      </TableCell>
      <TableCell>{formatShortDate(invoice.invoiceDate)}</TableCell>
      <TableCell>
        <DueDateCell invoice={invoice} />
      </TableCell>
      <TableCell align="right">{formatCurrencyValue(invoice.total)}</TableCell>
      <TableCell align="right">
        {hasBalance ? formatCurrencyValue(invoice.balance) : '\u2014'}
      </TableCell>
      <TableCell>
        <Tooltip
          arrow
          placement="top"
          title={
            <Stack spacing={0.5} sx={{ py: 0.5 }}>
              {tooltipLines.map((line) => (
                <Typography key={line} variant="caption">
                  {line}
                </Typography>
              ))}
            </Stack>
          }
        >
          <Box component="span">
            <InvoiceStatusChip status={invoice.status} />
          </Box>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
